// src/capabilities/CapabilitiesLayout.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  TablePagination,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import api from '../config/api';
import useStore from '../config/store';
import CapabilitiesModal from './CapabilitiesModal';
import CaseStudiesTab from './CaseStudiesTab';
import { useTranslation } from 'react-i18next';

const CapabilitiesLayout = () => {
  const [capabilities, setCapabilities] = useState([]);
  const [filteredCapabilities, setFilteredCapabilities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedCapability, setSelectedCapability] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [capabilityToDelete, setCapabilityToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // 0 = Competencias, 1 = Casos de estudio (catálogo independiente — PLAN_CASOS_ESTUDIO.md)
  const [activeTab, setActiveTab] = useState(0);

  const { authToken, showAlert, user, language, organization } = useStore.getState();
  const { t } = useTranslation();

  // Función para verificar permisos
  const canEdit = (capability) => {
    const userRole = user?.rol;

    // Super admin puede editar todo
    if (userRole === 'super-admin') return true;

    // Admin, admin-grafton y user solo pueden editar competencias de su organización
    if (['admin', 'admin-grafton', 'user'].includes(userRole)) {
      return capability.organizationId === organization;
    }

    return false;
  };

  const canDelete = (capability) => {
    const userRole = user?.rol;

    // Super admin puede eliminar todo
    if (userRole === 'super-admin') return true;

    // Admin, admin-grafton y user solo pueden eliminar competencias de su organización
    if (['admin', 'admin-grafton', 'user'].includes(userRole)) {
      return capability.organizationId === organization;
    }

    return false;
  };

  // Función para formatear fechas
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';

    try {
      let date;

      // Si es un timestamp de Firestore serializado {_seconds, _nanoseconds}
      if (dateValue && dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      }
      // Si es un timestamp de Firestore con toDate()
      else if (dateValue && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      }
      // Si es un objeto con seconds (formato Firestore)
      else if (dateValue && dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      }
      // Si ya es un objeto Date
      else if (dateValue instanceof Date) {
        date = dateValue;
      }
      // Si es un string de fecha
      else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      }
      // Si es un timestamp en milisegundos
      else if (typeof dateValue === 'number') {
        date = new Date(dateValue);
      }
      else {
        return 'N/A';
      }

      // Verificar que la fecha sea válida
      if (isNaN(date.getTime())) {
        return 'N/A';
      }

      // Formatear la fecha en español
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  // Función para filtrar competencias - MODIFICADA: SOLO busca por nombre
  const filterCapabilities = (capabilities, searchTerm) => {
    if (!searchTerm.trim()) {
      return capabilities;
    }

    const term = searchTerm.toLowerCase();
    return capabilities.filter((capability) => {
      return capability.name?.toLowerCase().includes(term);
    });
  };

  // Efecto para filtrar Y ORDENAR cuando cambia el término de búsqueda o las competencias
  useEffect(() => {
    const filtered = filterCapabilities(capabilities, searchTerm);

    // Ordenar alfabéticamente por nombre
    const sorted = filtered.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB, 'es', { sensitivity: 'base' });
    });

    setFilteredCapabilities(sorted);
    setPage(0);
  }, [capabilities, searchTerm]);

  // Función para limpiar búsqueda
  const clearSearch = () => {
    setSearchTerm('');
  };

  // API Functions - MODIFICADO: Enviar organizationId
  const getAllCapabilities = async () => {
    const response = await api.get(`/capabilities?language=${language}&organizationId=${organization}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  };

  const deleteCapability = async (id) => {
    const response = await api.delete(`/capabilities/${id}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  };

  useEffect(() => {
    loadCapabilities();
  }, []);

  const loadCapabilities = async () => {
    try {
      setLoading(true);
      const response = await getAllCapabilities();
      if (response.response && response.response.status_response === 'OK') {
        setCapabilities(response.response.data || []);
      } else {
        setError(t('capabilities.loadError'));
        showAlert(t('capabilities.loadError'), "warning");
      }
    } catch (err) {
      setError(t('generalApiError'));
      showAlert(t('generalApiError'), "error");
      console.error('Error loading capabilities:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (mode, capability = null) => {
    // Verificar permisos antes de abrir modal de edición
    if (mode === 'edit' && capability && !canEdit(capability)) {
      showAlert(t('capabilities.noPermissionToEdit'), "warning");
      return;
    }

    setModalMode(mode);
    setSelectedCapability(capability);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedCapability(null);
    setError(null);
  };

  const handleSaveModal = () => {
    loadCapabilities();
  };

  const handleDelete = (capability) => {
    // Verificar permisos antes de eliminar
    if (!canDelete(capability)) {
      showAlert(t('capabilities.noPermissionToDelete'), "warning");
      return;
    }

    setCapabilityToDelete(capability);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await deleteCapability(capabilityToDelete.id || capabilityToDelete.uid);

      if (response.response && response.response.status_response === 'OK') {
        await loadCapabilities();
        showAlert(t('capabilities.capabilityDeleteSuccess'), "success");
      } else {
        setError(t('capabilities.capabilityDeleteError'));
        showAlert(t('capabilities.capabilityDeleteError'), "error");
      }
    } catch (err) {
      setError(t('capabilities.capabilityDeleteError'));
      showAlert(t('capabilities.capabilityDeleteError'), "error");
      console.error('Error deleting capability:', err);
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
      setCapabilityToDelete(null);
    }
  };

  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setCapabilityToDelete(null);
  };

  if (loading && capabilities.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('capabilities.title')}
        </Typography>

        {activeTab === 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Barra de búsqueda compacta */}
          <TextField
            variant="outlined"
            placeholder={t('capabilities.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={clearSearch}
                    title={t('capabilities.clearSearch')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.12)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: 1,
                },
              }
            }}
          />

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal('create')}
            sx={{ borderRadius: 2 }}
          >
            {t('capabilities.newCapability')}
          </Button>
        </Box>
        )}
      </Box>

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label={t('capabilities.tabCapabilities')} />
        <Tab label={t('capabilities.tabCaseStudies')} />
      </Tabs>

      {activeTab === 1 && <CaseStudiesTab />}

      {activeTab === 0 && (
      <>
      {/* Contador de resultados */}
      {searchTerm && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {`${t('showing')} ${filteredCapabilities.length} ${t('of')} ${capabilities.length} ${t('capabilities.capabilities')}`}
          </Typography>
        </Box>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Mensaje cuando no hay resultados */}
      {filteredCapabilities.length === 0 && !loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {searchTerm
            ? `${t('capabilities.noCapabilitiesFound')} "${searchTerm}"`
            : t('capabilities.noCapabilities')
          }
        </Alert>
      )}

      {/* Tabla de competencias */}
      <TableContainer
        component={Paper}
        sx={{
          maxWidth: '100%',
          overflowX: 'auto',
          maxHeight: 'calc(100vh - 230px)',
          overflowY: 'auto',
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('name')}</TableCell>
              <TableCell>{t('capabilities.texts')}</TableCell>
              <TableCell>{t('lastModified')}</TableCell>
              <TableCell align="center">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCapabilities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((capability) => (
              <TableRow key={capability.id || capability.uid}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" fontWeight="medium">
                      {capability.name}
                    </Typography>
                    {!capability.organizationId && (
                      <Chip
                        label={language === 'es' ? 'Por defecto' : 'Default'}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${capability.casos?.length || capability.textos?.length || 0} ${t('capabilities.text')}`}
                    variant="outlined"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(capability.modified)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenModal('view', capability)}
                    title={t('show')}
                  >
                    <ViewIcon />
                  </IconButton>
                  {canEdit(capability) && (
                    <IconButton
                      size="small"
                      onClick={() => handleOpenModal('edit', capability)}
                      title={t('edit')}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                  {canDelete(capability) && (
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(capability)}
                      title={t('delete')}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredCapabilities.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        sx={{ mr: 6 }}
      />
      </>
      )}

      {/* Modal para crear/editar/ver */}
      <CapabilitiesModal
        open={openModal}
        onClose={handleCloseModal}
        mode={modalMode}
        selectedCapability={selectedCapability}
        onSave={handleSaveModal}
        user={user}
        authToken={authToken}
        showAlert={showAlert}
        language={language}
      />

      {/* Modal de confirmación para eliminar */}
      <Dialog open={openDeleteDialog} onClose={cancelDelete} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('capabilities.deleteTitle')}
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1">
            {t('capabilities.deleteConfirm')} <strong>"{capabilityToDelete?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('noWayBack')}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={cancelDelete} color="inherit">
            {t('cancel')}
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CapabilitiesLayout;